import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Card, CardActions, CardText } from 'react-md';
import Formsy from 'formsy-react';
import { Redirect } from 'react-router-dom';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { withSnackbar } from 'notistack';
import { isEqual, get } from 'lodash-es';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import MyInput from '../../Components/MyInput/MyInput';
import { I18nMarkdown } from '../../Components/Markdown';
import * as authActions from '../../Services/Auth/actions';
import * as themeActions from '../../Services/Theme/actions';
import CONFIG from '../../Config';
import { showAlerts } from '../Common/utils';
import Footer from '../Login/Footer';
import Header from '../Login/Header';

import './ForgetPassword.scss';

class ForgetPassword extends Component {
  constructor(props) {
    super(props);
    this.state = {
      login: '',
      canSubmit: false,
      redirectLogin: false,
    };
  }

  // workaround: autoComplete="off" does not work in Chrome
  // https://github.com/mlaursen/react-md/issues/709
  componentDidMount() {
    // load .env
    this.setState({
      app: {
        title: CONFIG.title,
        subTitleForgetPassword: CONFIG.subTitleForgetPassword,
        logoRef: CONFIG.logoRef,
        logoLink: CONFIG.logoLink,
      },
    });
  }

  componentDidUpdate(prevProps) {
    const { alerts: prevAlerts, loading: prevLoading } = prevProps;
    const { alerts, loading, enqueueSnackbar, strings } = this.props;
    if (!isEqual(prevLoading, loading) && !isEqual(prevAlerts, alerts)) {
      showAlerts({ enqueueSnackbar, alerts, strings });
    }
  }

  handleSubmit = model => {
    const { actions, auth } = this.props;
    const isReset = auth.get('isReset');

    if (isReset) {
      actions.logout();
      this.setState({ redirectLogin: true });
    } else {
      actions.postResetPassword({ login: model.login });
    }
  };

  disableButton = () => this.setState({ canSubmit: false });

  enableButton = () => this.setState({ canSubmit: true });

  renderForm = () => {
    const { auth, strings, form, theme, loading } = this.props;
    const { canSubmit, login, app = {} } = this.state;
    const isReset = auth.get('isReset');
    const isDisable = !canSubmit || loading;

    return (
      <div className="coog-form-login">
        <Header theme={theme} app={app} strings={strings} mode="forget_password" />
        <Card className="md-block-centered card-block">
          <CardText>
            <Formsy
              key="basic-coog-block"
              ref="forgetPasswordForm" // eslint-disable-line react/no-string-refs
              onValidSubmit={this.handleSubmit}
              onValid={this.enableButton}
              onInvalid={this.disableButton}
              autoComplete="off"
            >
              {isReset ? (
                <>
                  <div className="text">
                    <I18nMarkdown strings={strings} string="textMailSent" />
                  </div>
                  <CardActions className="md-cell md-cell--12">
                    <Button raised={false} flat secondary swapTheming type="submit" className="coog-login-button">
                      {form.closeLabel}
                    </Button>
                  </CardActions>
                </>
              ) : (
                <>
                  <div className="text">
                    <I18nMarkdown strings={strings} string="textForget" />
                  </div>
                  <MyInput
                    id="login"
                    name="login"
                    label={form.loginLabel}
                    placeholder={form.loginPlaceholder}
                    validationError={form.loginValidationError}
                    required
                    value={login}
                    autoComplete="nope"
                  />
                  <CardActions className="md-cell md-cell--12">
                    <Button
                      raised={isDisable}
                      disabled={isDisable}
                      flat={!isDisable}
                      secondary={!isDisable}
                      swapTheming={!isDisable}
                      type="submit"
                      className="coog-login-button"
                    >
                      {loading ? (
                        <div className="spinner">
                          <FontAwesomeIcon icon={faSpinner} size="lg" fixedWidth />
                        </div>
                      ) : (
                        form.nextLabel
                      )}
                    </Button>
                  </CardActions>
                </>
              )}
            </Formsy>
          </CardText>
        </Card>
        <Footer theme={theme} app={app} strings={strings} />
      </div>
    );
  };

  render() {
    const { redirectLogin } = this.state;

    if (redirectLogin) {
      return <Redirect to={{ pathname: '/' }} push />;
    }

    return this.renderForm();
  }
}

ForgetPassword.propTypes = {
  enqueueSnackbar: PropTypes.func.isRequired, // eslint-disable-line
  auth: ImmutablePropTypes.contains({
    loading: PropTypes.bool.isRequired,
    isReset: PropTypes.bool,
  }),
  actions: PropTypes.shape({
    logout: PropTypes.func.isRequired,
    postResetPassword: PropTypes.func.isRequired,
  }),
  form: PropTypes.shape({
    loginLabel: PropTypes.string,
    loginPlaceholder: PropTypes.string,
    loginValidationError: PropTypes.string,
    nextLabel: PropTypes.string,
    closeLabel: PropTypes.string,
  }),
  strings: PropTypes.shape({
    pageTitle: PropTypes.string,
    canonical: PropTypes.string,
    textForget: PropTypes.string,
    textMailSent: PropTypes.string,
  }),
  alerts: PropTypes.object, // eslint-disable-line
  loading: PropTypes.bool,
  theme: PropTypes.object, // eslint-disable-line
};

ForgetPassword.defaultProps = {
  auth: { loading: false },
  strings: {
    pageTitle: 'Coog Portal - Mot de passe oublié',
    canonical: 'http://www.coopengo.com/',
    'link.connectToGoogle.label': 'Connexion avec Google',
    textForget:
      'Vous avez **oublié** ou souhaitez **changer** votre mot de passe ?\n\nNous allons vous envoyer la marche à suivre par e-mail.',
    textMailSent:
      'Nous venons de vous adresser un mail qui vous permettra de définir un nouveau mot de passe.\n\nVous pouvez consulter dès à présent votre messagerie.',
  },
  actions: {},
  form: {
    loginLabel: 'Identifiant',
    loginPlaceholder: 'Identifiant',
    loginValidationError: 'Identifiant non valide',
    nextLabel: 'Poursuivre',
    closeLabel: 'Fermer',
  },
  loading: false,
  theme: {},
};

const mapStateToProps = state => {
  const auth = state.get('auth');
  const theme = state.get('theme').toJS();

  return {
    auth,
    loading: get(auth.toJS(), 'loading', false),
    alerts: state.getIn(['auth', 'alerts']),
    theme,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...authActions, ...themeActions }, dispatch),
});

export default withSnackbar(connect(mapStateToProps, mapDispatchToProps)(ForgetPassword));
